import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import {
  getCategories,
  getFilteredProducts,
  getAllVariants,
  getProductsWithFilteredVariants,
} from '../shared/helpers/index';
import FilterResults from '../shared/FilterResults';
import SelectedFilters from '../shared/SelectedFilters';
import FilterModal from '../shared/FilterModal';
import FilterAccordion from '../shared/FilterAccordion';
import FilterProductBanner from '../shared/FilterProductBanner';
import { filterBreakpointConfig, variantFilterBreakpointConfig } from './cardo.config';

// We only want the form to submit if JS fails
const onSubmit = e => e.preventDefault();

const onClickFilterResult = ({ product, action, onShowMoreClick }) => () => {
  if (onShowMoreClick) {
    onShowMoreClick({ ...product, action });
  }
};

const FilterLeft = ({
  categories: categoryNames = [],
  filterContent = {},
  filterData = {},
  cardoListProps = {},
  isVariantFilter,
  toggleFilter,
  setSelectedFilters,
  closeModal,
  currentBreakpoint,
  headingLevel = 4,
  selectedFilterIconSrc,
  accordionMinusIconSrc,
  accordionPlusIconSrc,
  refineIconSrc,
  modalCloseIconSrc,
  dataTestApply,
  dataTestClearAll,
  isOpen = true,
  showProductCount = false,
  id,
  onShopAllVariantsClick,
  onShowMoreClick,
  modalTriggerButtonWidth,
  modalTriggerButtonHeight,
}) => {
  const {
    products = [],
    categoryToggleTitle,
    categoryToggleProductText,
    selectedFiltersHeadingText,
    clearAllText,
    modalHeadingText,
    modalFormButtonText,
    truncatedLinkText,
    shopAllVariantsText,
  } = filterContent;

  const isLargeBreakpoint = currentBreakpoint.includes('large');
  const { selectedFilters = [], closedCategories = {} } = filterData;
  const categories = getCategories({
    categoryNames,
    products: isVariantFilter ? getAllVariants(products) : products,
    selectedFilters,
  });
  const productFilterFunction = isVariantFilter ?
    getProductsWithFilteredVariants : getFilteredProducts;

  const filteredProducts = productFilterFunction({
    products,
    selectedFilters,
    categories,
  });

  return (
    <div className="grid-container">
      <div className="grid-x grid-margin-x align-justify">
        <div className="cell xsmall-16 large-4">
          {!!selectedFilters.length && (
            <SelectedFilters
              selectedFilters={selectedFilters}
              clearAllText={clearAllText}
              toggleFilter={toggleFilter}
              setSelectedFilters={setSelectedFilters}
              headingLevel={headingLevel}
              selectedFiltersHeadingText={selectedFiltersHeadingText}
              selectedFilterIconSrc={selectedFilterIconSrc}
            />
          )}
          { /* No form action so that the url that is posted to is the same as the current one */ }
          <form onSubmit={onSubmit} method="post">
            { isLargeBreakpoint && (
              <FilterAccordion
                categories={categories}
                toggleFilter={toggleFilter}
                accordionMinusIconSrc={accordionMinusIconSrc}
                accordionPlusIconSrc={accordionPlusIconSrc}
                accordionItemTitleButtonExtraClass="filter__accordion-left-item-title"
                accordionItemBodyColumns={16}
                closedCategories={closedCategories}
                isOpen={isOpen}
                showProductCount={showProductCount}
              />
            )}
          </form>
          { !isLargeBreakpoint && (
            <FilterModal
              modalHeadingText={modalHeadingText}
              filteredProducts={filteredProducts}
              closeModal={closeModal}
              selectedFilters={selectedFilters}
              categories={categories}
              setSelectedFilters={setSelectedFilters}
              categoryToggleTitle={categoryToggleTitle}
              categoryToggleProductText={categoryToggleProductText}
              accordionMinusIconSrc={accordionMinusIconSrc}
              accordionPlusIconSrc={accordionPlusIconSrc}
              toggleFilter={toggleFilter}
              refineIconSrc={refineIconSrc}
              modalCloseIconSrc={modalCloseIconSrc}
              clearAllText={clearAllText}
              modalFormButtonText={modalFormButtonText}
              dataTestApply={dataTestApply}
              dataTestClearAll={dataTestClearAll}
              isOpen={isOpen}
              modalTriggerButtonWidth={modalTriggerButtonWidth}
              modalTriggerButtonHeight={modalTriggerButtonHeight}
            />
          )}
        </div>

        <div className="cell xsmall-16 large-12">
          { /* TODO: Implement Sort By dropdown here */}

          {isVariantFilter ? filteredProducts.map(product =>
            product.variants.length > 0 && (
              <div
                key={R.prop('uid', product)}
                className="variant-filter-results"
              >
                <FilterProductBanner
                  product={product}
                  linkUrl={R.prop('linkUrl', product)}
                  shopAllVariantsText={shopAllVariantsText}
                  truncatedLinkText={truncatedLinkText}
                  onShopAllVariantsClick={onShopAllVariantsClick}
                />
                <FilterResults
                  cardoListId={`${id}-${R.prop('uid', product)}`}
                  filteredProducts={product.variants}
                  cardoListProps={R.merge(
                    cardoListProps,
                    { showStarRating: false, showSwatches: false },
                  )}
                  breakpointConfig={variantFilterBreakpointConfig}
                  onShowMoreClick={onClickFilterResult({
                    product,
                    action: `${shopAllVariantsText} : view more`,
                    onShowMoreClick,
                  })}
                />
              </div>
            )) : (
              <FilterResults
                cardoListId={id}
                filteredProducts={filteredProducts}
                cardoListProps={cardoListProps}
                breakpointConfig={filterBreakpointConfig}
              />
          )}

        </div>
      </div>
    </div>
  );
};

FilterLeft.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  filterContent: PropTypes.objectOf(PropTypes.any).isRequired,
  filterData: PropTypes.shape({
    selectedFilters: PropTypes.arrayOf(PropTypes.shape({
      categoryName: PropTypes.string.isRequired,
      optionName: PropTypes.string.isRequired,
    })),
    closedCategories: PropTypes.objectOf(PropTypes.bool),
  }).isRequired,
  isVariantFilter: PropTypes.bool,
  toggleFilter: PropTypes.func.isRequired,
  setSelectedFilters: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  currentBreakpoint: PropTypes.string.isRequired,
  headingLevel: PropTypes.number,
  selectedFilterIconSrc: PropTypes.string.isRequired,
  accordionMinusIconSrc: PropTypes.string.isRequired,
  accordionPlusIconSrc: PropTypes.string.isRequired,
  refineIconSrc: PropTypes.string.isRequired,
  modalCloseIconSrc: PropTypes.string.isRequired,
  dataTestApply: PropTypes.string,
  dataTestClearAll: PropTypes.string,
  cardoListProps: PropTypes.objectOf(PropTypes.any),
  isOpen: PropTypes.bool,
  showProductCount: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  onShopAllVariantsClick: PropTypes.func,
  onShowMoreClick: PropTypes.func,
  modalTriggerButtonWidth: PropTypes.number,
  modalTriggerButtonHeight: PropTypes.number,
};

export default FilterLeft;
